import { useEffect, useState, useCallback } from "react";
import { api, useAuth } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Edit, Trash2, Search, Building2, Package, ArrowLeft,
  ChevronRight, TrendingUp, TrendingDown, Minus, CalendarDays
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_SUPPLIER = { name: "", notes: "" };
const EMPTY_PRODUCT = { product_name: "", price: "", quantity: "", order_date: new Date().toISOString().split("T")[0], notes: "" };

function PriceTrend({ entries }) {
  if (entries.length < 2) return null;
  const sorted = [...entries].sort((a, b) => (a.order_date || "").localeCompare(b.order_date || ""));
  const last = sorted[sorted.length - 1]?.price;
  const prev = sorted[sorted.length - 2]?.price;
  if (last == null || prev == null) return null;
  const diff = last - prev;
  if (diff > 0) return <span className="text-xs text-red-500 flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />+{diff.toFixed(2)}</span>;
  if (diff < 0) return <span className="text-xs text-emerald-600 flex items-center gap-0.5"><TrendingDown className="w-3 h-3" />{diff.toFixed(2)}</span>;
  return <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Minus className="w-3 h-3" />0.00</span>;
}

function SupplierList({ suppliers, selected, loading, search, onSearch, onSelect, onAdd, onEdit, onDelete, isAdmin, t }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />{t("suppliers.title")}
          </h1>
          <p className="text-xs text-muted-foreground">{suppliers.length} {t("suppliers.count")}</p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={onAdd} className="bg-primary text-white" data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-1" />{t("suppliers.add")}
          </Button>
        )}
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input className="pl-9 h-9" placeholder={t("suppliers.search")} value={search} onChange={(e) => onSearch(e.target.value)} data-testid="supplier-search-input" />
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {loading ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Cargando...</div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-12 space-y-2" data-testid="no-suppliers">
            <Building2 className="w-10 h-10 text-muted-foreground/20 mx-auto" />
            <p className="text-sm text-muted-foreground">{t("suppliers.noSuppliers")}</p>
            <p className="text-xs text-muted-foreground/60">{t("suppliers.noSuppliersHint")}</p>
          </div>
        ) : (
          suppliers.map((sup) => (
            <button
              key={sup.supplier_id}
              data-testid={`supplier-card-${sup.supplier_id}`}
              onClick={() => onSelect(sup)}
              className={`w-full text-left rounded-xl border px-4 py-3 transition-all hover:shadow-sm hover:border-primary/40 cursor-pointer ${selected?.supplier_id === sup.supplier_id ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "bg-white"}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{sup.name}</p>
                  {sup.notes && <p className="text-xs text-muted-foreground truncate mt-0.5">{sup.notes}</p>}
                </div>
                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                  {isAdmin && (
                    <>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); onEdit(sup); }} data-testid={`edit-supplier-${sup.supplier_id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(sup); }} data-testid={`delete-supplier-${sup.supplier_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                  <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function SupplierDetail({ supplier, products, loading, onBack, onAddProduct, onEditProduct, onDeleteProduct, isAdmin, t }) {
  const grouped = products.reduce((acc, p) => {
    const key = p.product_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1 lg:hidden" onClick={onBack} data-testid="back-to-suppliers">
            <ArrowLeft className="w-3 h-3" />{t("suppliers.back")}
          </button>
          <h2 className="text-lg font-bold text-foreground">{supplier.name}</h2>
          {supplier.notes && <p className="text-xs text-muted-foreground mt-0.5">{supplier.notes}</p>}
        </div>
        {isAdmin && (
          <Button size="sm" onClick={onAddProduct} className="bg-primary text-white ml-3 flex-shrink-0" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-1" />{t("suppliers.products.add")}
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Cargando productos...</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="text-center py-14 space-y-2" data-testid="no-products">
            <Package className="w-10 h-10 text-muted-foreground/20 mx-auto" />
            <p className="text-sm text-muted-foreground">{t("suppliers.products.noProducts")}</p>
            <p className="text-xs text-muted-foreground/60">{t("suppliers.products.noProductsHint")}</p>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={onAddProduct} className="mt-2">
                <Plus className="w-3 h-3 mr-1" />{t("suppliers.products.add")}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([productName, entries]) => {
              const sortedEntries = [...entries].sort((a, b) => (b.order_date || "").localeCompare(a.order_date || ""));
              const latestPrice = sortedEntries[0]?.price;
              return (
                <Card key={productName} className="shadow-sm">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-semibold">{productName}</CardTitle>
                        <CardDescription className="text-xs">
                          {entries.length} {t("suppliers.orderCount")}
                          {latestPrice != null && <span className="ml-2 text-foreground font-medium">· Ultimo: €{latestPrice.toFixed(2)}</span>}
                        </CardDescription>
                      </div>
                      <PriceTrend entries={entries} />
                    </div>
                  </CardHeader>
                  <CardContent className="px-4 pb-3">
                    <div className="space-y-2">
                      {sortedEntries.map((entry) => (
                        <div key={entry.supplier_product_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group" data-testid={`product-entry-${entry.supplier_product_id}`}>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
                            <CalendarDays className="w-3 h-3" />{entry.order_date || "—"}
                          </div>
                          <div className="flex-1 min-w-0">
                            {entry.price != null && <span className="font-semibold text-sm">€{entry.price.toFixed(2)}</span>}
                            {entry.quantity && <span className="text-xs text-muted-foreground ml-2">· {entry.quantity}</span>}
                            {entry.notes && <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{entry.notes}</p>}
                          </div>
                          {isAdmin && <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={() => onEditProduct(entry)} data-testid={`edit-product-${entry.supplier_product_id}`}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onDeleteProduct(entry)} data-testid={`delete-product-${entry.supplier_product_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Suppliers() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [mobileView, setMobileView] = useState("list");

  const [supplierForm, setSupplierForm] = useState(EMPTY_SUPPLIER);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [deleteSupplier, setDeleteSupplier] = useState(null);
  const [savingSupplier, setSavingSupplier] = useState(false);

  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/suppliers");
      const all = res.data || [];
      setSuppliers(search ? all.filter((s) => s.name.toLowerCase().includes(search.toLowerCase())) : all);
    } catch { toast.error("Error al cargar los proveedores"); }
    finally { setLoading(false); }
  }, [search]);

  const fetchAllSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/suppliers");
      setSuppliers(res.data || []);
    } catch { toast.error("Error al cargar los proveedores"); }
    finally { setLoading(false); }
  }, []);

  const fetchProducts = useCallback(async (supplierId) => {
    if (!supplierId) return;
    try {
      setLoadingProducts(true);
      const res = await api.get(`/suppliers/${supplierId}/products`);
      setProducts(res.data || []);
    } catch { toast.error("Error al cargar los productos"); }
    finally { setLoadingProducts(false); }
  }, []);

  useEffect(() => { fetchAllSuppliers(); }, [fetchAllSuppliers]);
  useEffect(() => { if (selected) fetchProducts(selected.supplier_id); }, [selected, fetchProducts]);

  const filteredSuppliers = search
    ? suppliers.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : suppliers;

  const selectSupplier = (sup) => { setSelected(sup); setMobileView("detail"); };

  const openAddSupplier = () => { if (!isAdmin) return; setEditingSupplier(null); setSupplierForm(EMPTY_SUPPLIER); setShowSupplierForm(true); };
  const openEditSupplier = (sup) => { if (!isAdmin) return; setEditingSupplier(sup); setSupplierForm({ name: sup.name, notes: sup.notes || "" }); setShowSupplierForm(true); };

  const saveSupplier = async () => {
    if (!isAdmin) return;
    if (!supplierForm.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSavingSupplier(true);
    try {
      if (editingSupplier) {
        const res = await api.put(`/suppliers/${editingSupplier.supplier_id}`, supplierForm);
        setSuppliers((prev) => prev.map((s) => s.supplier_id === editingSupplier.supplier_id ? res.data : s));
        if (selected?.supplier_id === editingSupplier.supplier_id) setSelected(res.data);
        toast.success("Proveedor actualizado");
      } else {
        const res = await api.post("/suppliers", supplierForm);
        setSuppliers((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Proveedor creado");
      }
      setShowSupplierForm(false);
    } catch { toast.error("Error al guardar"); }
    finally { setSavingSupplier(false); }
  };

  const confirmDeleteSupplier = async () => {
    if (!isAdmin) return;
    if (!deleteSupplier) return;
    try {
      await api.delete(`/suppliers/${deleteSupplier.supplier_id}`);
      setSuppliers((prev) => prev.filter((s) => s.supplier_id !== deleteSupplier.supplier_id));
      if (selected?.supplier_id === deleteSupplier.supplier_id) { setSelected(null); setMobileView("list"); }
      toast.success("Proveedor eliminado");
    } catch { toast.error("Error al eliminar"); }
    finally { setDeleteSupplier(null); }
  };

  const openAddProduct = () => { if (!isAdmin) return; setEditingProduct(null); setProductForm(EMPTY_PRODUCT); setShowProductForm(true); };
  const openEditProduct = (p) => { if (!isAdmin) return; setEditingProduct(p); setProductForm({ product_name: p.product_name, price: p.price ?? "", quantity: p.quantity || "", order_date: p.order_date || new Date().toISOString().split("T")[0], notes: p.notes || "" }); setShowProductForm(true); };

  const saveProduct = async () => {
    if (!isAdmin) return;
    if (!productForm.product_name.trim()) { toast.error("El nombre del producto es obligatorio"); return; }
    setSavingProduct(true);
    try {
      const payload = {
        product_name: productForm.product_name.trim(),
        price: productForm.price !== "" ? parseFloat(productForm.price) : null,
        quantity: productForm.quantity || "",
        order_date: productForm.order_date || null,
        notes: productForm.notes || "",
      };
      if (editingProduct) {
        await api.put(`/supplier-products/${editingProduct.supplier_product_id}`, payload);
        toast.success("Compra actualizada");
      } else {
        await api.post(`/suppliers/${selected.supplier_id}/products`, payload);
        toast.success("Compra registrada");
      }
      setShowProductForm(false);
      fetchProducts(selected.supplier_id);
    } catch { toast.error("Error al guardar"); }
    finally { setSavingProduct(false); }
  };

  const confirmDeleteProduct = async () => {
    if (!isAdmin) return;
    if (!deleteProduct) return;
    try {
      await api.delete(`/supplier-products/${deleteProduct.supplier_product_id}`);
      setProducts((prev) => prev.filter((p) => p.supplier_product_id !== deleteProduct.supplier_product_id));
      toast.success("Compra eliminada");
    } catch { toast.error("Error al eliminar"); }
    finally { setDeleteProduct(null); }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 hidden lg:block">
        <p className="text-xs text-muted-foreground">{t("suppliers.subtitle")}</p>
      </div>

      {/* Desktop: side-by-side */}
      <div className="hidden lg:grid lg:grid-cols-5 lg:gap-6" style={{ height: "calc(100vh - 160px)" }}>
        <div className="lg:col-span-2 bg-muted/20 rounded-2xl border p-4 flex flex-col overflow-hidden">
          <SupplierList suppliers={filteredSuppliers} selected={selected} loading={loading} search={search} onSearch={setSearch} onSelect={selectSupplier} onAdd={openAddSupplier} onEdit={openEditSupplier} onDelete={setDeleteSupplier} isAdmin={isAdmin} t={t} />
        </div>
        <div className="lg:col-span-3 bg-white rounded-2xl border p-4 flex flex-col overflow-hidden">
          {selected ? (
            <SupplierDetail supplier={selected} products={products} loading={loadingProducts} onBack={() => setSelected(null)} onAddProduct={openAddProduct} onEditProduct={openEditProduct} onDeleteProduct={setDeleteProduct} isAdmin={isAdmin} t={t} />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center space-y-3">
              <Building2 className="w-12 h-12 text-muted-foreground/20" />
              <p className="text-sm text-muted-foreground">{t("suppliers.selectHint")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: toggle view */}
      <div className="lg:hidden flex-1 flex flex-col">
        {mobileView === "list" ? (
          <SupplierList suppliers={filteredSuppliers} selected={selected} loading={loading} search={search} onSearch={setSearch} onSelect={selectSupplier} onAdd={openAddSupplier} onEdit={openEditSupplier} onDelete={setDeleteSupplier} isAdmin={isAdmin} t={t} />
        ) : selected ? (
          <SupplierDetail supplier={selected} products={products} loading={loadingProducts} onBack={() => setMobileView("list")} onAddProduct={openAddProduct} onEditProduct={openEditProduct} onDeleteProduct={setDeleteProduct} isAdmin={isAdmin} t={t} />
        ) : null}
      </div>

      {/* Supplier form dialog */}
      <Dialog open={isAdmin && showSupplierForm} onOpenChange={setShowSupplierForm}>
        <DialogContent className="sm:max-w-sm" data-testid="supplier-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? t("suppliers.editTitle") : t("suppliers.addTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs mb-1 block">{t("suppliers.nameLabel")}</Label>
              <Input value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} data-testid="supplier-name-input" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">{t("suppliers.notesLabel")}</Label>
              <Textarea rows={3} placeholder={t("suppliers.notesPlaceholder")} value={supplierForm.notes} onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })} className="resize-none" data-testid="supplier-notes-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSupplierForm(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveSupplier} disabled={savingSupplier} className="bg-primary text-white" data-testid="supplier-save-btn">
              {savingSupplier ? t("suppliers.saving") : editingSupplier ? t("common.update") : t("common.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product form dialog */}
      <Dialog open={isAdmin && showProductForm} onOpenChange={setShowProductForm}>
        <DialogContent className="sm:max-w-md" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingProduct ? t("suppliers.products.editTitle") : t("suppliers.products.addTitle")}</DialogTitle>
            <DialogDescription>{selected?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs mb-1 block">{t("suppliers.products.productLabel")}</Label>
              <Input placeholder={t("suppliers.products.productPlaceholder")} value={productForm.product_name} onChange={(e) => setProductForm({ ...productForm, product_name: e.target.value })} data-testid="product-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">{t("suppliers.products.priceLabel")}</Label>
                <Input type="number" step="0.01" placeholder="12.50" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} data-testid="product-price-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("suppliers.products.quantityLabel")}</Label>
                <Input placeholder={t("suppliers.products.quantityPlaceholder")} value={productForm.quantity} onChange={(e) => setProductForm({ ...productForm, quantity: e.target.value })} data-testid="product-quantity-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">{t("suppliers.products.orderDateLabel")}</Label>
              <Input type="date" value={productForm.order_date} onChange={(e) => setProductForm({ ...productForm, order_date: e.target.value })} data-testid="product-date-input" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">{t("suppliers.products.notesLabel")}</Label>
              <Input placeholder={t("suppliers.products.notesPlaceholder")} value={productForm.notes} onChange={(e) => setProductForm({ ...productForm, notes: e.target.value })} data-testid="product-notes-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowProductForm(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveProduct} disabled={savingProduct} className="bg-primary text-white" data-testid="product-save-btn">
              {savingProduct ? t("suppliers.saving") : editingProduct ? t("common.update") : t("common.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete supplier dialog */}
      <AlertDialog open={isAdmin && !!deleteSupplier} onOpenChange={(open) => !open && setDeleteSupplier(null)}>
        <AlertDialogContent data-testid="delete-supplier-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("suppliers.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("suppliers.deleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteSupplier} className="bg-destructive text-white">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete product dialog */}
      <AlertDialog open={isAdmin && !!deleteProduct} onOpenChange={(open) => !open && setDeleteProduct(null)}>
        <AlertDialogContent data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("suppliers.products.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("suppliers.products.deleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteProduct} className="bg-destructive text-white">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
